import { useState, useEffect, useRef } from 'react';
import { Formik, FormikState } from 'formik';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SearchIcon } from '../../assets/magnifying-glass-icon.svg';
import { ReactComponent as CloseCircleIcon } from '../../assets/close-circle-icon.svg';
import {
  ClearSearchButton,
  InputSearchContainer,
  InputListSearchContainer,
  SearchButtonContainer,
  SearchIconContainer,
} from './input-list-search.styles';
import { InputContainer } from '../form-field-text/form-field-text.styles';
import { ButtonSpinner } from '../button/button.styles';
import { MAX_CHAR_LENGTH } from '../form-field-text/form-field-text.component';
import InputMessage from '../input-message/input-message.component';

interface Props {
  initialValue?: string;
  placeholder?: string;
  isDisabled?: boolean;
  onChange?: (value: string) => void;
  onSearchSubmit?: (search: string) => void;
  maxLength?: number;
  isLoading?: boolean;
}

interface SearchFormValues {
  searchQuery: string;
}

const InputListSearch = ({
  initialValue,
  placeholder,
  isDisabled,
  onChange,
  onSearchSubmit,
  maxLength = MAX_CHAR_LENGTH,
  isLoading = false,
}: Props) => {
  const [inputSearchValue, setInputSearchValue] = useState(initialValue ?? '');
  const [showSearchIcon, setShowSearchIcon] = useState(true);
  const [showSearchButtonContainer, setShowSearchButtonContainer] =
    useState(false);

  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setInputSearchValue(initialValue ?? '');
  }, [initialValue]);

  useEffect(() => {
    setShowSearchIcon(!inputSearchValue);
    setShowSearchButtonContainer(!!inputSearchValue);
  }, [inputSearchValue]);

  const clearInput = (
    resetForm: (nextState?: Partial<FormikState<SearchFormValues>>) => void
  ) => {
    resetForm();
    setInputSearchValue('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
    onSearchSubmit?.('');
  };

  const handleFormSubmit = (values: SearchFormValues) => {
    onSearchSubmit?.(values.searchQuery);
  };

  const initialValues: SearchFormValues = {
    searchQuery: initialValue ?? '',
  };

  return (
    <InputListSearchContainer>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values) => {
          handleFormSubmit(values);
        }}>
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          resetForm,
          errors,
        }) => (
          <form onSubmit={handleSubmit}>
            <InputSearchContainer
              $isDisabled={isDisabled}
              $treatWholeInputAsSearch
              $hIconPosition='left'
              $showSearchIcon={showSearchIcon}>
              <InputContainer
                $error={false}
                $fontSize='1.4rem'>
                {inputSearchValue && (
                  <ClearSearchButton
                    $showSearchButtonContainer={showSearchButtonContainer}
                    onClick={() => clearInput(resetForm)}>
                    <CloseCircleIcon />
                  </ClearSearchButton>
                )}
                <input
                  maxLength={maxLength}
                  value={values.searchQuery}
                  ref={inputRef}
                  onChange={(event) => {
                    setInputSearchValue(event.target.value);
                    handleChange(event);
                    onChange?.(event.target.value);
                  }}
                  onBlur={handleBlur}
                  type='text'
                  id='searchQuery'
                  name='searchQuery'
                  placeholder={placeholder}
                />
                {showSearchIcon && (
                  <SearchIconContainer
                    $hIconPosition='left'
                    $disableSearchHover>
                    <SearchIcon />
                  </SearchIconContainer>
                )}
                {showSearchButtonContainer && (
                  <SearchButtonContainer
                    $contentStyle='thin'
                    $fullWidth={false}
                    onClick={() => handleSubmit()}
                    $isLoading={isLoading}>
                    {isLoading ? <ButtonSpinner /> : t('search')}
                  </SearchButtonContainer>
                )}
              </InputContainer>
              {errors.searchQuery && (
                <InputMessage type='error'>
                  {t(errors.searchQuery)}
                </InputMessage>
              )}
            </InputSearchContainer>
          </form>
        )}
      </Formik>
    </InputListSearchContainer>
  );
};

export default InputListSearch;
